<template>
  <ul class="facets-group default">
    <li
      v-for="facet of group.facets"
      :key="facet.slug"
      v-memo="[
        selectedFacets.includes(facet.slug),
        isFacetActive(facet.slug),
        shouldShowCount(facet.slug) ? counts[facet.slug] : -1,
      ]"
    >
      <facet-checkbox
        :name="facet.slug"
        :model-value="selectedFacets.includes(facet.slug)"
        :disabled="!isFacetActive(facet.slug)"
        @update:model-value="$emit('facetsChange', [[facet.slug, $event]])"
      >
        {{ facet.name }}
        <span v-show="shouldShowCount(facet.slug)" class="count">
          {{ counts[facet.slug] }}
        </span>
      </facet-checkbox>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { getCampsiteCategoriesPresenter } from '~/apps/categories/presenter-json/CampsiteCategoriesUpdaterJson'

interface Facet {
  name: string
  slug: string
}

interface FacetGroup {
  name: string
  slug: string
  facets: Facet[]
  hideEmpty: boolean
}

const props = withDefaults(
  defineProps<{
    group: FacetGroup
    counts: Record<string, number>
    initialFacetCounts: Record<string, number>
    selectedFacets: string[]
    selectedSubcategorySlug?: string
    showCount?: boolean
  }>(),
  {
    selectedSubcategorySlug: '',
    showCount: false,
  },
)

// Non-reactive: lang doesn't change during component lifetime (page navigation remounts)
const route = useRoute()
const lang = (route.params.lang as string) || 'en-gb'
const subcategoryFacetSlugs =
  getCampsiteCategoriesPresenter(lang).subcategoryFacetSlugs || []

const isSubcategoryFacet = (slug: string) => {
  return subcategoryFacetSlugs.includes(slug)
}

const isAnotherSubcategoryFacetSelected = (currentSlug: string) => {
  return (
    props.selectedSubcategorySlug !== '' &&
    props.selectedSubcategorySlug !== currentSlug
  )
}

function shouldShowCount(slug: string) {
  // Show count if showCount is enabled, the facet is active, and either:
  // 1. This facet is selected, or
  // 2. This is not a subcategory facet, or
  // 3. No other subcategory facet is selected
  return (
    props.showCount &&
    isFacetActive(slug) &&
    (props.selectedFacets.includes(slug) ||
      !isSubcategoryFacet(slug) ||
      !isAnotherSubcategoryFacetSelected(slug))
  )
}

function isFacetActive(slug: string) {
  // it's active if:
  // 1. We aren't showing counts at all, or
  // 2. It has a count value, or
  // 3. It's currently selected, or
  // 4. It's a subcategory facet (these should always be clickable)
  return (
    !props.showCount ||
    !!props.counts[slug] ||
    props.selectedFacets.includes(slug) ||
    isSubcategoryFacet(slug)
  )
}
</script>
